import { useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import useServerAuth from 'src/customHooks/useServerAuth'
import { updateSupportPractitionerEnabled } from 'src/supportPortal/services/practitionerOnboardingService'
import {
  canManagePractitionerEnabledState,
  derivePractitionerEnabled,
} from 'src/supportPortal/utils/practitionerStatus'

/**
 * Enable / disable control for a practitioner, gated to the roles that can actually manage
 * that practitioner type (ROLE_ADMIN everywhere, ROLE_THERAPIST_ADMIN for therapist/screener,
 * ROLE_CLINIC_SUCCESS_ADMIN for doctor). Renders nothing for roles without that access.
 *
 * Opens a confirm dialog that captures an optional reason — the backend writes the action,
 * actor, timestamp and reason into the practitioner access log. On success, `onUpdated` is
 * called with the refreshed practitioner payload and the new enabled boolean so the parent
 * can patch its local state.
 */
export default function PractitionerEnableDisableControl({
  practitioner,
  practitionerType = 'therapist',
  practitionerLabel = 'Practitioner',
  onUpdated,
  size = 'small',
  fullWidth = false,
}) {
  const { allRolesInBoolean } = useServerAuth()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const canManage = canManagePractitionerEnabledState(allRolesInBoolean, practitionerType)
  if (!canManage || !practitioner?.id) {
    return null
  }

  const isEnabled = derivePractitionerEnabled(practitioner)
  const nextEnabled = !isEnabled
  const actionLabel = nextEnabled ? `Enable ${practitionerLabel}` : `Disable ${practitionerLabel}`
  const practitionerName = practitioner.name || practitioner.firstName || 'this practitioner'

  function handleClose() {
    if (submitting) return
    setDialogOpen(false)
    setReason('')
  }

  async function handleConfirm() {
    setSubmitting(true)
    const resp = await updateSupportPractitionerEnabled(
      { practitionerId: practitioner.id, practitionerSlug: practitioner.slug },
      nextEnabled,
      reason,
    )
    setSubmitting(false)
    if (!resp.ok) {
      toast.error(
        resp.message ||
          `Unable to ${nextEnabled ? 'enable' : 'disable'} this ${practitionerLabel.toLowerCase()}.`,
      )
      return
    }
    toast.success(`${practitionerLabel} ${nextEnabled ? 'enabled' : 'disabled'}.`)
    setDialogOpen(false)
    setReason('')
    onUpdated?.(resp.data?.practitioner || {}, nextEnabled)
  }

  return (
    <>
      <Button
        variant={nextEnabled ? 'contained' : 'outlined'}
        color={nextEnabled ? 'success' : 'warning'}
        size={size}
        fullWidth={fullWidth}
        onClick={() => setDialogOpen(true)}
      >
        {actionLabel}
      </Button>
      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{actionLabel}?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {nextEnabled
                ? `This puts ${practitionerName} live again — bookings resume immediately.`
                : `This pauses ${practitionerName} — new bookings are held until re-enabled.`}
            </Typography>
            <TextField
              label="Reason (optional)"
              placeholder="Captured in the access log"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              multiline
              minRows={2}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={nextEnabled ? 'success' : 'warning'}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {submitting ? 'Saving…' : actionLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
